import type { ApiResponse } from '../types/api'
import type { PaginatedResult } from '../types/rooms'

export interface PaginationParams {
  limit: number
  offset: number
}

const parsePositiveInt = (value: string | null): number | null => {
  if (!value) return null
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return parsed
}

export const getNextPaginationParams = (
  nextUrl: string | null,
): PaginationParams | null => {
  if (!nextUrl) return null

  try {
    const parsedUrl = new URL(nextUrl, 'https://api.mangovoice.com')
    const limit = parsePositiveInt(parsedUrl.searchParams.get('limit'))
    const offset = Number.parseInt(
      parsedUrl.searchParams.get('offset') ?? '',
      10,
    )

    if (!limit || !Number.isFinite(offset) || offset < 0) return null
    return { limit, offset }
  } catch {
    return null
  }
}

interface FetchAllPaginatedResultsOptions<T, E> {
  initialLimit: number
  fetchPage: (
    params: PaginationParams,
  ) => Promise<ApiResponse<PaginatedResult<T>, E>>
}

/**
 * Fetch every page from a DRF-style paginated endpoint and flatten into one result.
 */
export const fetchAllPaginatedResults = async <T, E = unknown>({
  initialLimit,
  fetchPage,
}: FetchAllPaginatedResultsOptions<T, E>): Promise<PaginatedResult<T>> => {
  const firstResponse = await fetchPage({
    limit: initialLimit,
    offset: 0,
  })
  if (!firstResponse.ok) {
    return Promise.reject(firstResponse.data as E)
  }

  const firstPage = firstResponse.data as PaginatedResult<T>
  if (!firstPage.next) {
    return firstPage
  }

  const parsedNext = getNextPaginationParams(firstPage.next)
  const inferredLimit = parsedNext?.limit ?? firstPage.results.length
  const effectiveLimit = inferredLimit > 0 ? inferredLimit : initialLimit
  const firstRemainingOffset = parsedNext?.offset ?? effectiveLimit

  const remainingOffsets: number[] = []
  for (
    let offset = firstRemainingOffset;
    offset < firstPage.count;
    offset += effectiveLimit
  ) {
    remainingOffsets.push(offset)
  }

  const remainingResponses = await Promise.all(
    remainingOffsets.map((offset) =>
      fetchPage({
        limit: effectiveLimit,
        offset,
      }),
    ),
  )

  for (const response of remainingResponses) {
    if (!response.ok) {
      return Promise.reject(response.data as E)
    }
  }

  const allResults = [
    ...firstPage.results,
    ...remainingResponses.flatMap(
      (response) => (response.data as PaginatedResult<T>).results ?? [],
    ),
  ]

  return {
    count: firstPage.count,
    next: null,
    previous: null,
    results: allResults,
  }
}
